/**
 * The text of a case — Spec 025, D1. One set of fields for a new case and for
 * an edit, the words the card shows them under, and the checks a person sees
 * before the server refuses. A case is a «Пример решения» (R-08): how we solve
 * a task, never a real client's project — no invented names, numbers or
 * reviews, and the form says so.
 */

import { useRef, useState, type ChangeEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { ApiError, newIdempotencyKey } from "@/api/client";
import { Cases } from "@/api/resources";
import type { CaseContentIn, CaseDetailOut, CasePlaceKind, CaseStatus } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { TextArea, TextField } from "@/components/ui/Field";
import { Sheet } from "@/components/ui/Sheet";
import { Mark, Status } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";

export const EXAMPLE_LABEL = "Пример решения";

/** The long texts of a case, in the order the card reads them. */
export const CASE_TEXTS = [
  { key: "problem_md", label: "Задача клиента", hint: "В чём боль — 2–3 фразы" },
  { key: "solution_md", label: "Решение", hint: "Что делаем, по шагам" },
  { key: "how_md", label: "Как работает", hint: "Схема текстом: что с чем связано" },
  { key: "outcome_md", label: "Что получает клиент", hint: "Без выдуманных цифр — только то, что даёт решение" },
  { key: "demand_md", label: "Откуда спрос", hint: "Ссылки на реальные запросы, поисковые фразы, разговоры" },
] as const;

type TextKey = (typeof CASE_TEXTS)[number]["key"];
type Optional = "audience" | TextKey | "demo_url";
const OPTIONAL: Optional[] = ["audience", "problem_md", "solution_md", "how_md", "outcome_md", "demand_md", "demo_url"];

export type CaseDraft = Record<"title" | Optional, string>;

export const EMPTY_CASE: CaseDraft = {
  title: "", audience: "", problem_md: "", solution_md: "", how_md: "", outcome_md: "", demand_md: "", demo_url: "",
};

export const PLACE_KIND: Record<CasePlaceKind, string> = {
  telegram: "Telegram",
  vk: "ВКонтакте",
  site: "Сайт",
  proposal: "КП клиенту",
  other: "Другое",
};

/**
 * Where a case stands, in words (Spec 020 D3). On review it waits for a
 * person's decision — either of the two may take it — so it is «Нужно от тебя»;
 * the other states are properties of the record, a `Mark`.
 */
export function CaseState({ status }: { status: CaseStatus }) {
  if (status === "review") return <Status tone="needs" />;
  return <Mark>{status === "draft" ? "черновик" : status === "accepted" ? "принят" : "в архиве"}</Mark>;
}

/** An address a person may open: http(s) only — a stored `javascript:` stays text. */
export function webUrl(url: string | null | undefined): string | null {
  const value = url?.trim();
  return value && /^https?:\/\/\S+$/i.test(value) ? value : null;
}

/** The demo as a link: https only, as the server stores it — anything else stays text. */
export function demoLink(url: string | null | undefined): string | null {
  const value = url?.trim();
  return value && /^https:\/\/\S+$/i.test(value) ? value : null;
}

/** A demo opens outside the app: only an https link, as the server checks. */
export function demoUrlError(url: string): string | undefined {
  const value = url.trim();
  if (!value) return undefined;
  return /^https:\/\/\S+$/i.test(value) ? undefined : "Ссылка на демо — только https://";
}

export function draftOf(c: CaseDetailOut): CaseDraft {
  const draft: CaseDraft = { ...EMPTY_CASE, title: c.title };
  for (const key of OPTIONAL) draft[key] = c[key] ?? "";
  return draft;
}

/** A new case: the title and whatever is filled in, trimmed; empty fields are not sent. */
export function createBody(draft: CaseDraft): CaseContentIn & { title: string } {
  const body: CaseContentIn & { title: string } = { title: draft.title.trim() };
  for (const key of OPTIONAL) {
    const value = draft[key].trim();
    if (value) body[key] = value;
  }
  return body;
}

/** An edit sends only what changed; an emptied field goes as `null`, which clears it. */
export function patchOf(draft: CaseDraft, c: CaseDetailOut): CaseContentIn {
  const patch: CaseContentIn = {};
  const title = draft.title.trim();
  if (title && title !== c.title) patch.title = title;
  for (const key of OPTIONAL) {
    const value = draft[key].trim();
    if (value !== (c[key] ?? "").trim()) patch[key] = value || null;
  }
  return patch;
}

/** The fields of a case: the title is required, the rest may wait while it is a draft. */
export function CaseFields({ draft, onChange }: { draft: CaseDraft; onChange: (next: CaseDraft) => void }) {
  const set = (key: keyof CaseDraft) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    onChange({ ...draft, [key]: e.target.value });
  // «https:/» on its way is not an error yet; the button waits for a whole address.
  const typing = "https://".startsWith(draft.demo_url.trim().toLowerCase());
  return (
    <div className="space-y-3">
      <p className="text-meta text-p-text-muted">
        Кейс — пример решения: так мы решаем задачу. Без выдуманных клиентов, цифр и отзывов.
      </p>
      <TextField label="Название" value={draft.title} maxLength={200} placeholder="Например: запись клиентов через бота"
        onChange={set("title")} />
      <TextField label="Для кого" hint="Отрасль и тип компании" value={draft.audience} maxLength={300} onChange={set("audience")} />
      {CASE_TEXTS.map((field) => (
        <TextArea key={field.key} label={field.label} hint={field.hint} value={draft[field.key]} rows={3}
          maxLength={10_000} onChange={set(field.key)} />
      ))}
      <TextField label="Ссылка на демо" type="url" inputMode="url" autoCapitalize="off" placeholder="https://"
        value={draft.demo_url} maxLength={500} hint="Только https://, в демо — без реальных персональных данных"
        error={typing ? undefined : demoUrlError(draft.demo_url)} onChange={set("demo_url")} />
    </div>
  );
}

const LOST_REPLY = "Ответ сервера не дошёл. Нажми «Создать черновик» ещё раз — второй кейс не появится.";

/** The server refused: the same request would be refused again, so a resend is a new attempt. */
const refused = (e: unknown): e is ApiError =>
  e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 408;

/** A new case starts as a draft; the author sends it to review from its card. */
export function NewCaseSheet({ open, onClose, onCreated }: {
  open: boolean;
  onClose: () => void;
  onCreated: (created: CaseDetailOut) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<CaseDraft>(EMPTY_CASE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  // This form's retry key, as in Secrets: a resend after a lost reply reuses it and
  // gets the first case back; any edit is a new request with a new key.
  const attempt = useRef<string | null>(null);
  const change = (next: CaseDraft) => {
    attempt.current = null;
    setDraft(next);
  };

  const save = async () => {
    setError(undefined);
    setSaving(true);
    const key = (attempt.current ??= newIdempotencyKey());
    try {
      const created = await Cases.create(createBody(draft), key);
      attempt.current = null;
      setDraft(EMPTY_CASE);
      void qc.invalidateQueries({ queryKey: ["cases"] });
      toast.success(`Кейс №${created.number} — черновик`);
      onCreated(created);
    } catch (e) {
      // A refusal ends the attempt; after a lost reply the key waits for the resend.
      if (refused(e)) attempt.current = null;
      else void qc.invalidateQueries({ queryKey: ["cases"] }); // it may have landed
      setError(refused(e) ? e.message : LOST_REPLY);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Новый кейс"
      footer={
        <div className="space-y-2">
          {error && <p role="alert" className="text-meta text-p-blocked">{error}</p>}
          <Button block variant="strong" loading={saving} loadingText="Создаём…"
            disabled={!draft.title.trim() || Boolean(demoUrlError(draft.demo_url))} onClick={() => void save()}>
            Создать черновик
          </Button>
        </div>
      }
    >
      <CaseFields draft={draft} onChange={change} />
    </Sheet>
  );
}
